import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRightIcon, PhoneIcon, WhatsAppIcon } from './Icons';

const AREAS = [
  'Dubai Marina', 'JLT (Jumeirah Lake Towers)', 'JBR (Jumeirah Beach Residence)',
  'Downtown Dubai', 'Business Bay', 'DIFC', 'Palm Jumeirah',
  'Jumeirah 1', 'Jumeirah 2 & 3', 'Umm Suqeim', 'Al Barsha',
  'Mirdif', 'Arabian Ranches', 'Other',
];

const CONDITIONS = [
  'Back Pain', 'Neck Pain', 'Sports Injury', 'Post-Surgery Recovery',
  'Knee Pain', 'Shoulder Pain', 'Elderly Care & Mobility',
  'Sciatica', 'Frozen Shoulder', 'Other',
];

// preCondition: pre-fills the condition dropdown (e.g. "Back Pain")
// darkBg: true = white card on dark background
export default function BookingForm({ preCondition = '', darkBg = false }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: '', phone: '', condition: preCondition,
    area: '', gender: '', day: '',
  });

  const bg = darkBg ? 'rgba(255,255,255,0.08)' : 'var(--cream)';
  const cardBg = darkBg ? 'rgba(255,255,255,0.06)' : 'white';
  const labelColor = darkBg ? 'rgba(255,255,255,0.85)' : 'var(--text)';

  function set(field, val) { setForm(f => ({ ...f, [field]: val })); }

  function handleSubmit(e) {
    e.preventDefault();
    navigate('/confirmation', { state: { form } });
  }

  const inputStyle = {
    width: '100%', padding: '13px 16px',
    border: `1.5px solid ${darkBg ? 'rgba(255,255,255,0.2)' : 'var(--cream-dark)'}`,
    borderRadius: 'var(--radius)', fontSize: '15px',
    background: darkBg ? 'rgba(255,255,255,0.08)' : 'white',
    color: darkBg ? 'white' : 'var(--text)',
    outline: 'none',
  };

  return (
    <form onSubmit={handleSubmit} style={{ width: '100%' }}>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px', marginBottom:'14px' }}>
        {/* Name */}
        <div>
          <label style={{ ...labelStyle, color: labelColor }}>Your name</label>
          <input
            className="form-input" type="text" placeholder="Sarah Ahmed"
            value={form.name} onChange={e => set('name', e.target.value)} required
            style={inputStyle}
          />
        </div>
        {/* Phone */}
        <div>
          <label style={{ ...labelStyle, color: labelColor }}>Phone number</label>
          <input
            className="form-input" type="tel" placeholder="050 123 4567"
            value={form.phone} onChange={e => set('phone', e.target.value)} required
            style={inputStyle}
          />
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px', marginBottom:'14px' }}>
        {/* Condition */}
        <div>
          <label style={{ ...labelStyle, color: labelColor }}>Condition</label>
          <select
            value={form.condition} onChange={e => set('condition', e.target.value)} required
            style={{ ...inputStyle, cursor:'pointer' }}
          >
            <option value="">Select condition</option>
            {CONDITIONS.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        {/* Area */}
        <div>
          <label style={{ ...labelStyle, color: labelColor }}>Your area in Dubai</label>
          <select
            value={form.area} onChange={e => set('area', e.target.value)} required
            style={{ ...inputStyle, cursor:'pointer' }}
          >
            <option value="">Select area</option>
            {AREAS.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px', marginBottom:'22px' }}>
        {/* Gender */}
        <div>
          <label style={{ ...labelStyle, color: labelColor }}>Therapist preference</label>
          <select
            value={form.gender} onChange={e => set('gender', e.target.value)}
            style={{ ...inputStyle, cursor:'pointer' }}
          >
            <option value="">No preference</option>
            <option value="female">Female therapist</option>
            <option value="male">Male therapist</option>
          </select>
        </div>
        {/* When */}
        <div>
          <label style={{ ...labelStyle, color: labelColor }}>When do you need us?</label>
          <select
            value={form.day} onChange={e => set('day', e.target.value)} required
            style={{ ...inputStyle, cursor:'pointer' }}
          >
            <option value="">Select timing</option>
            <option value="today">Today (subject to availability)</option>
            <option value="tomorrow">Tomorrow</option>
            <option value="this_week">This week</option>
            <option value="next_week">Next week</option>
          </select>
        </div>
      </div>

      <button type="submit" className="btn-primary" style={{ width:'100%', justifyContent:'center', fontSize:'16px', padding:'15px' }}>
        Request a Home Visit <ArrowRightIcon size={16} color="white" />
      </button>

      {/* Privacy note */}
      <p style={{ textAlign:'center', fontSize:'12px', color: darkBg ? 'rgba(255,255,255,0.5)' : 'var(--text-light)', marginTop:'12px', lineHeight:'1.5' }}>
        Your information is private and used only to arrange your visit.
        We will never share or sell your details.
      </p>

      {/* Or contact */}
      <div style={{ display:'flex', justifyContent:'center', alignItems:'center', gap:'16px', marginTop:'18px', flexWrap:'wrap' }}>
        <a href="tel:+97100000000" style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'14px', fontWeight:'600', color: darkBg ? 'rgba(255,255,255,0.8)' : 'var(--navy)' }}>
          <PhoneIcon size={14} color={darkBg ? 'rgba(255,255,255,0.8)' : 'var(--teal)'} />
          Call instead
        </a>
        <span style={{ color: darkBg ? 'rgba(255,255,255,0.2)' : 'var(--cream-dark)' }}>|</span>
        <a href="https://wa.me/97100000000" target="_blank" rel="noopener noreferrer" style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'14px', fontWeight:'600', color:'#25D366' }}>
          <WhatsAppIcon size={14} color="#25D366" />
          WhatsApp us
        </a>
      </div>
    </form>
  );
}

const labelStyle = {
  display: 'block', fontSize: '13px',
  fontWeight: '600', marginBottom: '6px',
};